import React from 'react';
import { Container, Grid, GridItem, Heading, Text } from '@charlietango/ui';
import { rem } from '../../styles/rem';

import Anchor from '../../components/Anchor/Anchor';
import { colors } from '../../styles/colors';
import { fixedBottom } from '../../styles/fixedBottom';
import { FontWeight, TextVariant } from '../../styles/typography';
import LinkViewModel from '../../view-models/LinkViewModel';
import LogoFooter from './LogoFooter';

export type FooterProps = {
  title?: string;
  description?: string;
  subTitle?: string;
  subDescription?: string;
  links?: {
    title?: string;
    items?: LinkViewModel[];
  };
  contact?: {
    title?: string;
    address?: string;
    phone?: LinkViewModel;
    email?: LinkViewModel;
  };
};

const ulStyle = {
  listStyle: 'none',
  paddingLeft: 0,
  marginTop: rem(16),
  marginBottom: 0,
};

const liStyle = {
  marginTop: rem(16),
  maxWidth: '100%',
  color: colors.base.Text.PrimaryStatic,
};

const linkStyle = {
  color: colors.base.Text.PrimaryStatic,
  textDecoration: 'underline',
  textDecorationThickness: '2px',
  textUnderlineOffset: '2px',
  '&:hover, &:focus': { color: colors.base.Text.PrimaryStatic },
};

//H3 and paragraph differ from the default typography
const H3Style = {
  lineHeight: rem(32),
  mb: 4,
  fontWeight: FontWeight.Medium,
  color: colors.base.Text.PrimaryStatic,
  fontSize: rem(24),
};

function splitAddress(address: string) {
  const postalCodeMatch = address.match(/\b\d{4}\b/);

  if (!postalCodeMatch || postalCodeMatch.index === undefined) {
    return [address];
  }

  return [
    address.slice(0, postalCodeMatch.index).replace(/[\s,]+$/, ''),
    address.slice(postalCodeMatch.index).trim(),
  ];
}

type ListItemProps = {
  children?: React.ReactNode;
};

const ListItem = ({ children }: ListItemProps) => {
  return (
    <span
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: '100%',
        overflowWrap: 'break-word',
        fontSize: rem(20),
      }}
    >
      {children}
    </span>
  );
};

function Footer({
  title,
  description,
  subTitle,
  subDescription,
  links,
  contact,
}: FooterProps) {
  return (
    <footer
      sx={{
        color: colors.base.Text.PrimaryStatic,
        backgroundColor: colors.components.footer.Background.Default,
      }}
    >
      <Container
        sx={{
          pt: rem(80),
          pb: [rem(80), rem(80), rem(80)],
          "html[data-declaration-drawer='true'] &": {
            pb: [
              fixedBottom.declarationDrawerCollapsedHeight,
              fixedBottom.declarationDrawerCollapsedHeight,
              rem(80),
            ],
          },
          px: [rem(20), rem(20), rem(40)],
        }}
      >
        <Grid gridColumns={[8, 8, 12]} gridGap={[rem(32), rem(40), rem(40)]}>
          <GridItem
            sx={{
              mb: [rem(24), rem(16), 0],
            }}
            size={[7 / 8, 7 / 8, 1 / 3]}
          >
            <LogoFooter />
            <Text
              variant={TextVariant.BodyMd}
              sx={{
                mt: rem(16),
                color: colors.base.Text.PrimaryStatic,
              }}
            >
              {subDescription}
            </Text>
          </GridItem>
          {links && (
            <GridItem size={[7 / 8, 4 / 8, 1 / 3]} sx={{ mb: [rem(24), 0, 0] }}>
              {links.title && (
                <Heading variant={TextVariant.BodyXlBold} sx={H3Style}>
                  {links.title}
                </Heading>
              )}
              {links?.items && (
                <ul sx={ulStyle}>
                  {links.items.map((item, index) => (
                    <li key={index} sx={liStyle}>
                      <Anchor prefetch {...item} sx={linkStyle}>
                        <ListItem>{item.label}</ListItem>
                      </Anchor>
                    </li>
                  ))}
                </ul>
              )}
            </GridItem>
          )}
          {contact && (
            <GridItem size={[7 / 8, 4 / 8, 1 / 3]} sx={{ mb: [rem(24), 0, 0] }}>
              {contact?.title && (
                <Heading variant={TextVariant.BodyXlBold} sx={H3Style}>
                  {contact.title}
                </Heading>
              )}
              <ul sx={ulStyle}>
                {contact?.email?.href && (
                  <li sx={liStyle}>
                    <Anchor
                      href={contact.email.href}
                      sx={{
                        ...linkStyle,
                        textDecoration: 'underline',
                      }}
                    >
                      <ListItem>{contact.email.label}</ListItem>
                    </Anchor>
                  </li>
                )}
                {contact.address && (
                  <li sx={liStyle}>
                    <ListItem>
                      <span>
                        {splitAddress(contact.address).map((line, index) => (
                          <React.Fragment key={line}>
                            {index > 0 && <br />}
                            {line}
                          </React.Fragment>
                        ))}
                      </span>
                    </ListItem>
                  </li>
                )}
                {contact?.phone?.href && (
                  <li sx={liStyle}>
                    <Anchor
                      href={contact.phone.href}
                      sx={{
                        ...linkStyle,
                        textDecoration: 'underline',
                      }}
                    >
                      <ListItem>{contact.phone.label}</ListItem>
                    </Anchor>
                  </li>
                )}
              </ul>
            </GridItem>
          )}
        </Grid>
      </Container>
    </footer>
  );
}

export default Footer;
